import { useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';

import AppLoader from '@/components/AppLoader';
import { useSelector } from '@/redux/helper';
import { isEducatorsPath, toEducatorsURL } from '@/shared/educatorsApp';

import { CANCELLED_MESSAGE, isPopupReturn, reportToOpenerAndClose, withReport } from './popup';

/**
 * Where Stripe sends an abandoned checkout.
 *
 * A Speedy Flow checkout closes itself and tells the opener, which puts the visitor back on the
 * product choice with their previous pick intact. Nothing was charged, so there is no entitlement
 * to go looking for and no reason to linger.
 *
 * An educators checkout hops to that origin first and reports from there, because its opener is not
 * listening to this one (see `shared/educatorsApp`). No pause on this path: the head start exists
 * for a webhook, and an abandoned checkout does not produce one.
 */
const CheckoutCancelled = () => {
  const educatorsAppURL = useSelector((state) => state.remoteConfig?.educatorsAppURL);
  /**
   * `educatorsAppURL` arrives with the remote config, a beat after mount, so this effect can run
   * more than once. Reporting is once-only, so the first pass to reach it claims it.
   */
  const reportedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('co-redirect');
    const destination = redirect?.startsWith('/') ? redirect : '/home';

    // The marker says how this tab was meant to come back; the opener says how it actually can.
    // A blocked popup is demoted to this same tab and keeps the marker, so with no opener this is
    // an ordinary full-page cancel and simply goes where one goes. See `CheckoutCompleted`.
    const opener = window.opener as Window | null;
    if (isPopupReturn(window.location.search) && !!opener && !opener.closed) {
      const decoded = redirect ? decodeURIComponent(redirect) : undefined;
      const educators = isEducatorsPath(decoded);

      // Worth waiting for the config, because the alternative is not a fallback: posting from this
      // origin to an opener on the educators one is a message nobody receives. Let the effect run
      // again when the URL lands.
      if (educators && !educatorsAppURL) {
        return;
      }

      if (reportedRef.current) {
        return;
      }
      reportedRef.current = true;

      const hop = educators ? toEducatorsURL(educatorsAppURL, decoded as string) : undefined;
      if (hop) {
        window.location.replace(withReport(hop, CANCELLED_MESSAGE));
        return;
      }
      reportToOpenerAndClose(CANCELLED_MESSAGE, 0, () => route(destination));
      return;
    }

    route(destination);
  }, [educatorsAppURL]);

  return <AppLoader />;
};

export default CheckoutCancelled;
